/**
 * InServiceTrainingCard
 *
 * Reusable panel for displaying — and (for supervisors) managing — an aide's
 * in-service training log. Used on:
 *   • HRDetailPage (supervisors can schedule / mark complete / edit target)
 *   • AideDashboard (aide reads their own; can mark "complete" themselves)
 *
 * Props:
 *   userId    - aide user id whose log to display
 *   variant   - "supervisor" | "aide"  (controls Schedule + Target controls)
 *   onChange  - optional callback after a mutation, so parent can refresh
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  GraduationCap, Plus, CheckCircle2, XCircle, Loader2, Calendar, Target,
  BookOpen, Clock, AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_STYLES = {
  scheduled: 'bg-[#DBEAFE] text-[#1E40AF]',
  completed: 'bg-[#DCFCE7] text-[#15803D]',
  missed: 'bg-[#FEE2E2] text-[#991B1B]',
  cancelled: 'bg-[#F1F5F9] text-[#64748B]',
};

export default function InServiceTrainingCard({ userId, variant = 'supervisor', onChange }) {
  const isSupervisor = variant === 'supervisor';
  const [data, setData] = useState({ trainings: [], summary: null });
  const [topics, setTopics] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTarget, setShowTarget] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: '', topic: '', hours: 1, scheduled_for: '', lms_course_id: '', notes: '',
  });
  const [targetHours, setTargetHours] = useState(12);

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = variant === 'aide' && !userId
        ? `${API_URL}/api/inservice/my`
        : `${API_URL}/api/inservice/aides/${userId}`;
      const [d, top] = await Promise.all([
        axios.get(endpoint, { headers: authHeader() }),
        axios.get(`${API_URL}/api/inservice/required-topics`, { headers: authHeader() }),
      ]);
      setData(d.data);
      setTopics(top.data?.topics || []);
      if (d.data?.summary?.annual_hours_target) {
        setTargetHours(d.data.summary.annual_hours_target);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load in-service log');
    } finally { setLoading(false); }
  };

  const loadCourses = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/lms/courses?status=published`, { headers: authHeader() });
      setCourses(r.data || []);
    } catch { /* lms is optional */ }
  };

  useEffect(() => { load(); loadCourses(); /* eslint-disable-next-line */ }, [userId, variant]);

  const openSchedule = () => {
    setForm({ title: '', topic: '', hours: 1, scheduled_for: '', lms_course_id: '', notes: '' });
    setShowSchedule(true);
  };

  const submitSchedule = async () => {
    if (!form.title.trim()) { toast.error('Training title required'); return; }
    setBusy(true);
    try {
      const payload = { ...form, hours: parseFloat(form.hours) || 0 };
      // Empty strings → null so backend doesn't treat them as set
      ['scheduled_for', 'lms_course_id', 'topic'].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      await axios.post(`${API_URL}/api/inservice/aides/${userId}`, payload,
        { headers: authHeader() });
      toast.success('Training scheduled — aide notified');
      setShowSchedule(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Schedule failed');
    } finally { setBusy(false); }
  };

  const markComplete = async (t) => {
    if (!window.confirm(`Mark "${t.title}" as completed?`)) return;
    try {
      await axios.patch(`${API_URL}/api/inservice/${t.id}`,
        { status: 'completed' }, { headers: authHeader() });
      toast.success('Marked complete');
      load(); onChange?.();
    } catch (err) { toast.error('Update failed'); }
  };

  const markMissed = async (t) => {
    if (!window.confirm(`Mark "${t.title}" as missed?`)) return;
    try {
      await axios.patch(`${API_URL}/api/inservice/${t.id}`,
        { status: 'missed' }, { headers: authHeader() });
      toast.success('Marked missed');
      load(); onChange?.();
    } catch (err) { toast.error('Update failed'); }
  };

  const cancelTraining = async (t) => {
    if (!window.confirm(`Cancel "${t.title}"? It will be removed from the log.`)) return;
    try {
      await axios.delete(`${API_URL}/api/inservice/${t.id}`, { headers: authHeader() });
      toast.success('Removed');
      load(); onChange?.();
    } catch (err) { toast.error('Delete failed'); }
  };

  const saveTarget = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/inservice/aides/${userId}/target`,
        { annual_hours_target: parseFloat(targetHours) || 0 },
        { headers: authHeader() });
      toast.success('Annual hours target updated');
      setShowTarget(false);
      load(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const summary = data.summary;
  const trainings = data.trainings || [];
  const upcoming = trainings.filter(t => t.status === 'scheduled');
  const past = trainings.filter(t => t.status !== 'scheduled');

  return (
    <Card className="border-[#E2E8F0]" data-testid="inservice-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-[#0E7490]" />
          In-Service Training
        </CardTitle>
        <div className="flex items-center gap-2">
          {isSupervisor && (
            <>
              <Button size="sm" variant="outline"
                      onClick={() => setShowTarget(true)}
                      data-testid="inservice-target-btn">
                <Target className="w-3.5 h-3.5 mr-1" /> Target
              </Button>
              <Button size="sm" onClick={openSchedule}
                      className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                      data-testid="schedule-training-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Schedule
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-center py-6 text-xs text-[#64748B]">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : (
          <>
            {/* Progress strip */}
            {summary && (
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid="inservice-summary">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-semibold text-[#0A2342]">
                    {summary.hours_completed_ytd}h / {summary.annual_hours_target}h
                    <span className="font-normal text-[#64748B] ml-1">YTD</span>
                  </span>
                  <span className="text-[#64748B]">
                    {summary.required_topics_completed} / {summary.required_topics_total} required topics
                  </span>
                </div>
                <Progress value={summary.percent_complete} className="h-2"
                          data-testid="inservice-progress-bar" />
                {summary.hours_remaining > 0 && (
                  <div className="text-[10px] text-[#94A3B8] mt-1">
                    {summary.hours_remaining}h remaining this year
                  </div>
                )}
              </div>
            )}

            {/* Required-topic checklist */}
            {summary?.topic_progress && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1.5">
                  Required topics
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {summary.topic_progress.map(t => (
                    <div key={t.code}
                         className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${
                           t.completed
                             ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                             : 'border-[#E2E8F0] bg-white text-[#64748B]'
                         }`}
                         data-testid={`topic-${t.code}`}>
                      {t.completed
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        : <AlertTriangle className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" />}
                      <span className="truncate">{t.label}</span>
                      <span className="ml-auto text-[10px] text-[#94A3B8]">{t.default_hours}h</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming sessions */}
            {upcoming.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1.5">
                  Upcoming ({upcoming.length})
                </div>
                <div className="space-y-1">
                  {upcoming.map(t => (
                    <TrainingRow key={t.id} t={t}
                                 onComplete={markComplete}
                                 onMissed={markMissed}
                                 onCancel={cancelTraining}
                                 canManage={isSupervisor || (variant === 'aide')} />
                  ))}
                </div>
              </div>
            )}

            {/* Past sessions */}
            {past.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1.5">
                  History ({past.length})
                </div>
                <div className="space-y-1">
                  {past.map(t => (
                    <TrainingRow key={t.id} t={t}
                                 onComplete={null}
                                 onMissed={null}
                                 onCancel={isSupervisor ? cancelTraining : null}
                                 canManage={isSupervisor} />
                  ))}
                </div>
              </div>
            )}

            {trainings.length === 0 && (
              <div className="text-center py-6 text-xs text-[#64748B]" data-testid="inservice-empty">
                No in-service trainings yet.
                {isSupervisor && ' Click Schedule to add the first one.'}
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Schedule dialog */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="max-w-lg" data-testid="schedule-training-dialog">
          <DialogHeader>
            <DialogTitle>Schedule in-service training</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Title *</Label>
              <Input value={form.title}
                     onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                     placeholder="e.g. Q2 Fall-prevention refresher"
                     data-testid="schedule-title-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Topic</Label>
                <select value={form.topic}
                        onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm"
                        data-testid="schedule-topic-select">
                  <option value="">— Pick a required topic —</option>
                  {topics.map(t => (
                    <option key={t.code} value={t.code}>{t.label} ({t.default_hours}h)</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Hours</Label>
                <Input type="number" step="0.25" min="0" value={form.hours}
                       onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
                       data-testid="schedule-hours-input" />
              </div>
              <div>
                <Label>Scheduled for</Label>
                <Input type="datetime-local" value={form.scheduled_for}
                       onChange={e => setForm(f => ({ ...f, scheduled_for: e.target.value }))}
                       data-testid="schedule-date-input" />
              </div>
              <div>
                <Label>LMS course (optional)</Label>
                <select value={form.lms_course_id}
                        onChange={e => setForm(f => ({ ...f, lms_course_id: e.target.value }))}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm"
                        data-testid="schedule-course-select">
                  <option value="">— None —</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes}
                        onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Location, instructor, materials…"
                        data-testid="schedule-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(false)}>Cancel</Button>
            <Button onClick={submitSchedule} disabled={busy}
                    className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                    data-testid="schedule-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Schedule & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Target dialog */}
      <Dialog open={showTarget} onOpenChange={setShowTarget}>
        <DialogContent className="max-w-sm" data-testid="target-dialog">
          <DialogHeader>
            <DialogTitle>Annual hours target</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Required hours per year</Label>
            <Input type="number" step="0.5" min="0" max="200"
                   value={targetHours}
                   onChange={e => setTargetHours(e.target.value)}
                   data-testid="target-hours-input" />
            <p className="text-[11px] text-[#94A3B8]">
              CMS regulation 484.80 requires HHAs to complete at least 12 hours per year.
              Some states require more (e.g., NY requires 24).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTarget(false)}>Cancel</Button>
            <Button onClick={saveTarget} disabled={busy}
                    className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white"
                    data-testid="target-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save target
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TrainingRow({ t, onComplete, onMissed, onCancel, canManage }) {
  const styleClass = STATUS_STYLES[t.status] || STATUS_STYLES.scheduled;
  const dateLabel = t.completed_at
    ? `Completed ${new Date(t.completed_at).toLocaleDateString()}`
    : t.scheduled_for
      ? `Scheduled ${new Date(t.scheduled_for).toLocaleString()}`
      : '—';
  return (
    <div className="flex items-start justify-between gap-2 px-2 py-2 rounded border border-[#F1F5F9] hover:bg-[#F8FAFC] text-xs"
         data-testid={`inservice-row-${t.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[#0A2342] truncate flex items-center gap-1.5">
          <BookOpen className="w-3 h-3 text-[#0E7490] shrink-0" /> {t.title}
        </div>
        <div className="text-[10px] text-[#64748B] flex items-center gap-2 flex-wrap mt-0.5">
          {t.topic_label && <span>{t.topic_label}</span>}
          {t.lms_course_title && (
            <Link to={`/lms/courses/${t.lms_course_id}`}
                  className="text-[#0E7490] hover:underline">
              {t.lms_course_title}
            </Link>
          )}
          <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{t.hours}h</span>
          <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" />{dateLabel}</span>
        </div>
        {t.notes && <div className="text-[10px] text-[#94A3B8] mt-0.5 truncate">{t.notes}</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Badge className={`text-[9px] uppercase tracking-wide ${styleClass}`} variant="outline">
          {t.status}
        </Badge>
        {canManage && onComplete && (
          <button onClick={() => onComplete(t)}
                  title="Mark complete"
                  className="text-emerald-600 hover:bg-emerald-50 rounded p-1"
                  data-testid={`mark-complete-${t.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        )}
        {canManage && onMissed && (
          <button onClick={() => onMissed(t)}
                  title="Mark missed"
                  className="text-[#991B1B] hover:bg-red-50 rounded p-1"
                  data-testid={`mark-missed-${t.id}`}>
            <XCircle className="w-3.5 h-3.5" />
          </button>
        )}
        {canManage && onCancel && (
          <button onClick={() => onCancel(t)}
                  title="Remove"
                  className="text-[#94A3B8] hover:text-[#991B1B] hover:bg-red-50 rounded p-1"
                  data-testid={`remove-${t.id}`}>
            <XCircle className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
